import CloseIcon from "@mui/icons-material/Close";
import NavigateNext from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { getNavOrgAthleteGoal } from "apps/web/src/pages/Auth/Org/Athlete/AthletePaths";
import {
  AthleteForCustomTagFragment,
  GoalConfigurationTemplateTableItemFragment,
  useAssignedAthleteForGoalConfigurationTemplateQuery,
} from "apps/web/src/types";
import { sortByFn } from "libs/utils/sort/dist";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import NamedTagChipList from "../../Tags/NamedTagChipList";
import { getCompareString, sortGroupAndFilterAthletesByTeam } from "../../Tags/utils";

interface ViewAssignedAthletesDialogProps {
  open: boolean;
  onClose: () => void;
  goalConfigTemplate: GoalConfigurationTemplateTableItemFragment;
}

const AthleteItemContent = ({ athlete, onClick }: { athlete: AthleteForCustomTagFragment; onClick: () => void }) => {
  return (
    <Box
      sx={theme => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: 1,
        borderColor: theme.palette.greyscaleLight[200],
        my: 1,
        height: 38,
        borderRadius: 1,
      })}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", ml: 2 }}>
        <Typography variant="body2">{`${athlete.lastName}, ${athlete.firstName} `}</Typography>
        <NamedTagChipList
          namedTags={athlete.myNamedTagsOffsetConnection.edges}
          namedTagsCount={athlete.myNamedTagsOffsetConnection.pageInfo.total}
        />
      </Box>
      <IconButton onClick={onClick} size="small">
        <NavigateNext />
      </IconButton>
    </Box>
  );
};

const ViewAssignedAthletesDialog = ({ open, onClose, goalConfigTemplate }: ViewAssignedAthletesDialogProps) => {
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();
  const { data } = useAssignedAthleteForGoalConfigurationTemplateQuery({
    variables: {
      goalConfigurationTemplateId: goalConfigTemplate.id,
    },
    fetchPolicy: "network-only",
  });

  const athletes: readonly AthleteForCustomTagFragment[] = data?.assignedAthleteForGoalConfigurationTemplate || [];

  const groupedAthletes = useMemo(
    () =>
      sortGroupAndFilterAthletesByTeam({
        athletes,
        sortFn: athleteList => sortByFn(athleteList, getCompareString),
        filterFn: athletesList =>
          athletesList.filter(athlete => {
            return getCompareString(athlete).includes(searchText.toLowerCase());
          }),
      }),
    [athletes, searchText]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3">View Assigned Athletes</Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={theme => ({
            display: "flex",
            borderRadius: 1,
            border: 1,
            p: 2,
            flexDirection: "column",
            borderColor: theme.palette.greyscaleLight[200],
          })}
        >
          <Typography
            variant="body1Medium"
            sx={{ mb: 0.5 }}
          >{`${goalConfigTemplate.category.defaultEmoji} ${goalConfigTemplate.name}`}</Typography>
          <Typography variant="body2Medium">Assigned Athletes: {athletes.length}</Typography>
        </Box>
        <TextField
          sx={{ mb: 1, width: "100%", mt: 1 }}
          placeholder="Search Athletes"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={e => setSearchText(e.target.value)}
          value={searchText}
        />
        {groupedAthletes.map(({ id, name: teamName, athletes: athletesForTeam }) => (
          <div key={id}>
            <Typography variant="body2Medium">
              {teamName} ({athletesForTeam.length})
            </Typography>
            {athletesForTeam.map(athlete => (
              <AthleteItemContent key={athlete.id} athlete={athlete} onClick={() => navigate(getNavOrgAthleteGoal(athlete.id))} />
            ))}
          </div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAssignedAthletesDialog;
